import { useState, useEffect, useRef } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { getUserProfile } from '@/services/api'

interface ProtectedRouteProps {
  children: React.ReactNode
  requirePayment?: boolean
}

/**
 * Route guard that redirects unauthenticated users to login
 * and unpaid users to payment (if requirePayment is true)
 * Demo mode bypasses all checks for design review purposes
 *
 * When requirePayment is true, verifies payment status with backend
 * to prevent local storage manipulation
 */
export function ProtectedRoute({ children, requirePayment = false }: ProtectedRouteProps) {
  const { isAuthenticated, hasPaid, demoMode, setHasPaid } = useAuthStore()
  const location = useLocation()
  const [isVerifying, setIsVerifying] = useState(requirePayment && isAuthenticated && hasPaid)
  const [verificationFailed, setVerificationFailed] = useState(false)
  const hasVerified = useRef(false)

  // Verify payment status with backend when payment is required
  useEffect(() => {
    async function verifyPayment() {
      // Skip if already verified, not authenticated, or in demo mode
      if (hasVerified.current || !requirePayment || !isAuthenticated || demoMode) {
        setIsVerifying(false)
        return
      }

      // Only verify if local state says paid
      if (!hasPaid) {
        setIsVerifying(false)
        return
      }

      hasVerified.current = true

      try {
        const profile = await getUserProfile()

        // If backend says not paid but local says paid, update local state
        if (!profile.hasPaid) {
          console.warn('[ProtectedRoute] Backend payment verification failed - local state mismatch')
          setHasPaid(false)
          setVerificationFailed(true)
        }
      } catch (error) {
        // On API error, trust local state but log the error
        console.error('[ProtectedRoute] Payment verification API error:', error)
      } finally {
        setIsVerifying(false)
      }
    }

    verifyPayment()
  }, [requirePayment, isAuthenticated, hasPaid, demoMode, setHasPaid])

  // Demo mode bypasses all auth/payment checks
  if (demoMode) {
    return <>{children}</>
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Show loading state while verifying payment
  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FFF8F5] via-[#FFFEF1] to-[#F0FAFA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#46C0C6] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Verifying access...</p>
        </div>
      </div>
    )
  }

  // Backend verification failed - redirect to payment
  if (verificationFailed) {
    return <Navigate to="/payment" replace />
  }

  // Authenticated but not paid, and payment is required
  if (requirePayment && !hasPaid) {
    return <Navigate to="/payment" replace />
  }

  return <>{children}</>
}
